import {
    CapacityTextStatus,
    type SegmentOption,
    TripInfoComponent,
    type TripPackageDetails,
} from '../types';
import type TripPriceType from '../TripPriceType';
import TripPackage from '../TripPackage';
import { FlightStatus as FsFlightStatus, CabinClasses } from './types';
import { FlightSegment } from './FlightSegment';
import FlightAutoPriceClass from './FlightAutoPriceClass';
import FlightPriceClass from './FlightPriceClass';
import Flight from './Flight';
import {
    CabinClassRequest,
    type FlightPackageDto,
    type FlightsMetaDto,
    type FlightSpecialOfferDto,
} from '@clients/FlightApi';
import { type AutomaticReserveDemandItemRequest } from '@clients/PricetrackApi';
import ItineraryOrder from '@itinerary/ItineraryOrder';
import {
    ExtraInfoIcons,
    ExtraInfoLang,
    type CardDescription,
    type CardLabel,
    type PassengerCount,
} from '@itinerary/types';
import { ReserveType } from '@utils/itinerary';

/**
 * The status of flight package
 */
export enum FlightPackageStatus {
    OK,
    Full,
    NotEnoughCapacity,
    Cancelled,
    NonElectronic,
    Closed,
    NonSelectable,
    CallToReserve,
}

export default class FlightPackage extends TripPackage<
    Flight,
    FlightPriceClass
> {
    public flightId: string | null = null;

    override reserveType = ReserveType.Flight;
    /**
     * Points of the trip packages, based on passengers' opinions
     */
    override points: number | null = null;
    providerName = '';

    override get hasNeedHotelForm() {
        return this.international;
    }

    override prices: TripPriceType<FlightPriceClass>[] = [];

    override specialOffers: FlightSpecialOfferDto[] = [];

    override get showCapacityOnCard() {
        return CapacityTextStatus.low === this.capacityStatus;
    }

    override get labels(): CardLabel[] {
        const labels: CardLabel[] = [];
        const { featureFlags } = storeToRefs(usePostHog());
        const enabled = featureFlags.value['trip-new-card'];
        if (this.cabinClass === CabinClassRequest.Business) {
            labels.push({
                title: 'بیزینس',
                backgroundColor: enabled
                    ? 'var(--blue-100)'
                    : 'var(--yellow-200)',
                color: enabled ? 'var(--blue-700)' : 'var(--yellow-700)',
            });
        } else if (this.cabinClass !== CabinClassRequest.Unknown) {
            if (enabled) {
                const newClasses = CabinClasses.map(label => {
                    if (label.Id === CabinClassRequest.EconomyPlus) {
                        return {
                            ...label,
                            Title: 'اکونومی+',
                            backgroundColor: 'var(--blue-100)',
                            color: 'var(--blue-700)',
                        };
                    }

                    if (label.Id === CabinClassRequest.Economy) {
                        return {
                            ...label,
                            backgroundColor: 'var(--cool-gray-100)',
                            color: 'var(--cool-gray-800)',
                        };
                    }

                    if (label.Id === CabinClassRequest.Business) {
                        return {
                            ...label,
                            backgroundColor: 'var(--blue-100)',
                            color: 'var(--blue-700)',
                        };
                    } else {
                        return {
                            ...label,
                            backgroundColor: 'var(--cool-gray-100)',
                            color: 'var(--cool-gray-800)',
                        };
                    }
                });
                const cabinClass = newClasses.find(
                    ({ Id }) => Id === this.cabinClass
                );
                if (cabinClass?.Title) {
                    labels.push({
                        title: cabinClass.Title,
                        color: cabinClass.color,
                        backgroundColor: cabinClass.backgroundColor,
                    });
                }
            } else {
                labels.push({
                    title:
                        CabinClasses.find(({ Id }) => Id === this.cabinClass)
                            ?.Title || '',
                });
            }
        }
        if (this.segments[0]?.trips[0]?.vehicleInfo.type) {
            labels.push({
                title: this.segments[0]?.trips[0]?.vehicleInfo.type,
            });
        }
        labels.push(
            ...this.specialOffers.map(label => ({
                title: label.Title || '',
                backgroundColor: enabled
                    ? undefined
                    : label.BadgeColorOnCard || '',
                // icons cannot be fetched from server
                // iconName: label.Icon || '',
            }))
        );
        if (this.defaultPrice && !this.defaultPrice.hasBaggage) {
            labels.push({
                title: 'بدون بار رایگان',
                backgroundColor: enabled ? 'var(--red-100)' : undefined,
                color: enabled ? 'var(--red-500)' : undefined,
            });
        }
        if (this.providerName) {
            labels.push({
                title: this.providerName,
            });
        }
        return labels;
    }

    override get packageInfoDescription(): CardDescription[] {
        return this.specialOffers
            .filter(s => !!s.Description)
            .map(label => ({
                title: label.Title || '',
                backgroundColor: label.BadgeColorOnCard || '',
                // icon cannot be fetched from server
                // iconName: label.Icon || '',
                description: label.Description!,
            }));
    }

    override haveSmEvent = true;

    override segments: FlightSegment[] = [];

    override order = new ItineraryOrder(0, 0);

    override totalCount = 0;

    private readonly _isFull: boolean = false;

    status: FlightPackageStatus = FlightPackageStatus.OK;

    reservable_ = false;

    override international = false;

    override needsLoginForDiscount = false; // ToDo:get it form server to work fine :)

    override key = '';

    override get callToReserve() {
        return this.status === FlightPackageStatus.CallToReserve;
    }

    override get raiseTitle() {
        return `انتخاب کلاس پرواز ${`${this.origin} به ${this.destination}`}`;
    }

    override get hasPassengerDelete() {
        return !!this.passengerCountEditTypes.length;
    }

    override get tripPackageDetails(): TripPackageDetails[] {
        if (this.international && this.extraCancellationTerm) {
            return [
                ...super.tripPackageDetails,
                {
                    title: 'شرایط مسیر و ویزا',
                    component: TripInfoComponent.ExtraTerms,
                },
            ];
        }
        return super.tripPackageDetails;
    }

    constructor(
        flight: FlightPackageDto | null = null,
        paxCount: PassengerCount | null = null,
        meta: FlightsMetaDto | null = null,
        defaultPriceIndex = 0,
        public isCapacityTrackCampaign = false,
        uniqueId = ''
    ) {
        super();
        if (flight == null || meta == null) {
            return;
        }
        this.concreteType = ReserveType.Flight;
        this.hasAutoReserve = !!flight.AutomaticReserve;

        this.flightId = flight.Id || null;
        this.havePriceTrack = !!flight.MustAppearInPriceTrack;
        this.haveDecreasingPriceNotifier =
            !!flight.MustAppearInNotifyOnDecreasingPrice;
        const international = meta.RoutesInfo?.some(
            r => r.Origin?.IsInternational || r.Destination?.IsInternational
        );
        this.international = !!international;
        if (
            (flight.UnchangeableInfCount ||
                !flight.PassengerCountChangeAllowed) &&
            !flight.AutomaticReserve
        ) {
            this.passengerCountEditTypes = [];
        }
        // set prices
        this.prices = [
            {
                classes:
                    flight.Prices?.map(
                        p =>
                            new FlightPriceClass(
                                p,
                                international,
                                paxCount || undefined
                            )
                    ) || [],
            },
        ];
        if (flight.AutomaticReserve && !flight.Prices?.length) {
            this.prices = [
                {
                    classes: [
                        new FlightAutoPriceClass(
                            international,
                            paxCount || undefined
                        ),
                    ],
                },
            ];
        }
        if (this.prices.length > 0) {
            this.defaultPrice = this.selectedPrice =
                this.prices[0].classes[defaultPriceIndex];
        }
        const defaultPrice = flight.Prices?.[defaultPriceIndex];
        this.cancellationTerms =
            defaultPrice?.CancellationTernEntities?.map(term => ({
                title: term.Title || '',
                amount: ![null, undefined, -1].includes(term.PercentAmount)
                    ? `٪${term.PercentAmount}`
                    : formatMoney(term.Amount),
                isFull: term.PercentAmount === 100,
                isZero: term.PercentAmount === 0 || term.Amount === 0,
                specifiedByProvider:
                    (term.PercentAmount === 100 && defaultPrice.IsCharter) ||
                    (term.Amount === null && term.PercentAmount === null),
            })) || [];
        this.providerName = defaultPrice?.ProviderName || '';
        this.specialOffers = defaultPrice?.FlightSpecialOffers || [];
        this.extraCancellationTerm = defaultPrice?.ExtraTerms || '';
        // set segments
        this.segments =
            flight.Segments?.map(segment => {
                const trips =
                    segment.Legs?.map(
                        (leg, index) =>
                            new Flight(
                                leg,
                                meta,
                                index,
                                !international,
                                this.defaultPrice?.cabinClass
                            )
                    ) || [];
                // noinspection PointlessBooleanExpressionJS
                this.separateExtraTermsTab = !!international;
                const options: SegmentOption[] = [];
                if (this.defaultPrice?.priceClass) {
                    options.push({
                        title: `کلاس نرخی ${this.defaultPrice?.priceClass}`,
                    });
                }
                const stopCount =
                    trips.length +
                    trips.reduce(
                        (acc: any, t: { stops: any }) => acc + t.stops,
                        0
                    ) -
                    1;
                const stops = `${trips
                    .slice(1)
                    .map(t => t.originCityName)
                    .join(' ' + 'و')} `;
                return new FlightSegment(
                    trips,
                    options,
                    trips.length === 1
                        ? 'بدون توقف'
                        : `${stopCount} توقف در ${stops}`,
                    true,
                    segment.ConnectionTime,
                    segment.TotalTime || undefined,
                    international,
                    []
                );
            }) || [];
        this.points = flight.Score || 0;

        if (
            this.segments.some(s =>
                s.trips.some(trip => trip.status === FsFlightStatus.Cancelled)
            )
        ) {
            this.status = FlightPackageStatus.Cancelled;
            // not applicable yet
            // } else if (this.capacity < this.totalCount) {
            //  this.status = FlightPackageStatus.NotEnoughCapacity;
        } else if (flight.Prices?.every(p => (p.Capacity || 0) < 0)) {
            this.status = FlightPackageStatus.Full;
        }
        this.totalCount =
            (paxCount && paxCount.adultCount + paxCount.childCount) || 0;
        this.reservable_ =
            !!flight.Prices?.some(p => (p.Capacity || 0) > 0) &&
            this.status !== FlightPackageStatus.Cancelled;
        this.order = new ItineraryOrder(
            this.departureDateTime?.getTime() || 0,
            this.singlePayable,
            undefined,
            this.segments
                .map(segment => segment.duration)
                .reduce(
                    (previousValue, currentValue) =>
                        previousValue + currentValue,
                    0
                )
        );
        this.key =
            flight.Prices?.map(value => value.Id || generateUUID()).join() ||
            generateUUID();
        this.uniqueId =
            flight.Prices?.[defaultPriceIndex]?.UniqueId || uniqueId;
        if (flight.PhoneSale) {
            this.status = FlightPackageStatus.CallToReserve;
        }
        this._isFull = !!flight.Prices?.every(price => price.Capacity === 0);
    }

    override get reservable() {
        return this.reservable_;
    }

    override get reserveButtonEnabled() {
        if (this.defaultPrice && this.defaultPrice.capacity < this.totalCount) {
            return false;
        }
        return super.reserveButtonEnabled;
    }

    override get autoReserveDemandParams(): AutomaticReserveDemandItemRequest {
        const [{ corporationInfo }] = this.segments[0].digest;
        return {
            arrivalTime: formatISO(new Date(this.arrivalDateTime)).slice(0, -1),
            departureTime: formatISO(new Date(this.departureDateTime)).slice(
                0,
                -1
            ),
            serviceType: 0,
            providerId: corporationInfo.code,
            providerName: corporationInfo.title,
            serviceNumber: this.serviceNumber,
            serviceId: this.serviceId?.toString(),
        };
    }

    override get extraInfo() {
        return (this?.defaultPrice as FlightPriceClass)?.baggage &&
            (this?.defaultPrice as FlightPriceClass).international
            ? {
                  text: (this.defaultPrice as FlightPriceClass)
                      ?.baggageTypeText,
                  icon: ExtraInfoIcons.baggage,
                  lang: (this.defaultPrice as FlightPriceClass)
                      ?.providerBaggageType
                      ? ExtraInfoLang.en
                      : ExtraInfoLang.fa,
              }
            : undefined;
    }

    override get reserveError() {
        if (this.defaultPrice && this.defaultPrice.capacity < this.totalCount) {
            return 'ظرفیت موجود کمتر از تعداد مسافران شماست!';
        }
        return super.reserveError;
    }

    override get multiSegmentDepartureTitle() {
        return `${this.origin} به ${this.destination}`;
    }

    override get multiSegmentReturnTitle() {
        return `پرواز برگشت (${this.destination} به ${this.origin})`;
    }

    override get isFull() {
        return this._isFull;
    }

    get cabinClass() {
        return this.defaultPrice?.cabinClass || CabinClassRequest.Unknown;
    }

    override get buyTooltip(): string {
        switch (this.status) {
            case FlightPackageStatus.Full:
                return 'ظرفیت این پرواز در حال حاضر تکمیل بوده و امکان رزرو ندارد چنانچه ظرفیتی آزاد شود به صورت آنلاین نمایش داده خواهد شد';
            case FlightPackageStatus.NotEnoughCapacity:
                return 'ظرفیت این پرواز در حال حاضر کمتر از تعداد نفرات جستجو شده توسط شما می باشد';
            case FlightPackageStatus.Cancelled:
                return 'این پرواز از طرف شرکت هواپیمایی برگزارکننده کنسل شده است و امکان صدور بلیط ندارد';
            case FlightPackageStatus.NonElectronic:
                return 'بلیط این پرواز امکان رزرو غیرحضوری ندارد و می بایست از طریق دفاتر محلی حضورا خریداری شود';
            case FlightPackageStatus.Closed:
                return 'لیست مسافرین این پرواز نهایی شده است جهت تهیه بلیط اضطراری هر چه سریعتر به فرودگاه مراجعه نمایید';
            case FlightPackageStatus.NonSelectable:
                return `در حال حاضر خرید این پرواز امکان پذیر نمی باشد. لطفا پرواز دیگری انتخاب کنید یا منتظر بمانید.`;
            case FlightPackageStatus.CallToReserve:
                return `لطفا برای بررسی ظرفیت و رزرو این پرواز با شماره 61169000-021 تماس بگیرید`;
        }
        return '';
    }

    override get buyText(): string {
        if (this.autoReserveEnabled) {
            return 'رزرو خودکار';
        }
        switch (this.status) {
            case FlightPackageStatus.Full:
                return 'تکمیل ظرفیت';
            case FlightPackageStatus.NotEnoughCapacity:
                return 'ظرفیت ناکافی';
            case FlightPackageStatus.Cancelled:
                return 'کنسل شده';
            case FlightPackageStatus.NonElectronic:
                return 'رزرو حضوری';
            case FlightPackageStatus.CallToReserve:
                return 'تماس بگیرید';
            default:
                return super.buyText;
        }
    }

    override get canTrackPackageCapacity(): boolean {
        if (this.isCapacityTrackCampaign) {
            return [FlightPackageStatus.Full].includes(this.status);
        }
        return [FlightPackageStatus.OK, FlightPackageStatus.Full].includes(
            this.status
        );
    }
}
